import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Heart, MessageCircle, MoreHorizontal, Trash2, Users } from 'lucide-react'

import { AttachmentCard } from '@/components/attachment-card'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import {
  useAddComment,
  useDeleteComment,
  useDeletePost,
  usePostComments,
  useToggleLike,
  type Post,
} from '@/features/sosial/api'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatRelative } from '@/lib/format'

/**
 * PostCard menampilkan satu kiriman.
 *
 * Bentuknya mengikuti lini masa berutas: tanpa bingkai kartu, avatar pada
 * lajur kirinya sendiri, dan garis rambut sebagai pemisah antar kiriman.
 * Bingkai per kiriman membuat lini masa terbaca sebagai tumpukan kotak;
 * tanpa bingkai, yang terbaca adalah percakapan yang mengalir.
 */
export function PostCard({ post, tampilkanGrup = true }: { post: Post; tampilkanGrup?: boolean }) {
  const [komentarTerbuka, setKomentarTerbuka] = useState(false)
  const [menuTerbuka, setMenuTerbuka] = useState(false)

  const suka = useToggleLike()
  const hapus = useDeletePost()
  const toast = useToast()

  return (
    <article className="-mx-4 flex gap-3 border-b border-line px-4 py-3 md:-mx-6 md:px-6">
      <div className="flex shrink-0 flex-col items-center gap-2">
        <Link to="/app/orang/$userId" params={{ userId: post.author.id }}>
          <Avatar name={post.author.full_name} src={post.author.avatar_url} size="utas" />
        </Link>

        {/* Garis utas menyambung ke bawah selama komentarnya terbuka,
            sehingga tanggapan terbaca sebagai bagian dari kiriman yang sama,
            bukan sebagai kiriman baru. */}
        {komentarTerbuka ? <span className="w-px flex-1 bg-line" aria-hidden /> : null}
      </div>

      <div className="min-w-0 flex-1">
        <header className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <Link
                to="/app/orang/$userId"
                params={{ userId: post.author.id }}
                className="font-semibold text-ink hover:underline"
              >
                {post.author.full_name}
              </Link>
              <span className="text-subtle">{formatRelative(post.created_at)}</span>
            </div>

            {post.author.headline || (tampilkanGrup && post.group_slug) ? (
              <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted">
                {post.author.headline ? <span className="truncate">{post.author.headline}</span> : null}
                {tampilkanGrup && post.group_slug ? (
                  <Link
                    to="/app/grup/$slug"
                    params={{ slug: post.group_slug }}
                    className="inline-flex items-center gap-1 text-brand hover:underline"
                  >
                    <Users className="size-3" />
                    {post.group_name}
                  </Link>
                ) : null}
              </p>
            ) : null}
          </div>

          {post.can_delete ? (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMenuTerbuka((n) => !n)}
                aria-label="Tindakan lain"
                aria-expanded={menuTerbuka}
                className="-my-1.5 grid size-8 place-items-center rounded-full text-subtle transition hover:bg-sunken hover:text-ink"
              >
                <MoreHorizontal className="size-4" />
              </button>

              {menuTerbuka ? (
                <>
                  {/* Lapisan tak terlihat menutup menu saat diklik di mana
                      pun. Tanpa itu, menu yang terbuka tetap menempel sampai
                      tombolnya ditekan lagi. */}
                  <div className="fixed inset-0 z-10" onClick={() => setMenuTerbuka(false)} aria-hidden />
                  <div className="absolute right-0 top-9 z-20 w-40 overflow-hidden rounded-xl border border-line bg-raised shadow-soft">
                    <button
                      type="button"
                      disabled={hapus.isPending}
                      onClick={() => {
                        setMenuTerbuka(false)
                        hapus.mutate(post.id, {
                          onSuccess: () => toast.success('Kiriman dihapus'),
                          onError: (error) =>
                            toast.error(
                              'Gagal menghapus',
                              error instanceof ApiError ? error.message : undefined,
                            ),
                        })
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-semibold text-danger transition hover:bg-sunken"
                    >
                      <Trash2 className="size-4" />
                      Hapus kiriman
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </header>

        {post.body ? (
          <p className="mt-0.5 whitespace-pre-line text-ink">{post.body}</p>
        ) : null}

        {post.attachment ? <AttachmentCard lampiran={post.attachment} className="mt-3" /> : null}

        {post.images.length > 0 ? <GaleriPostingan gambar={post.images} /> : null}

        <div className="-ml-2 mt-2 flex items-center gap-1">
          <TombolAksi
            label={post.liked ? 'Batalkan suka' : 'Suka'}
            aktif={post.liked}
            jumlah={post.like_count}
            onClick={() => suka.mutate({ postId: post.id, liked: post.liked })}
          >
            <Heart className={cn('size-5', post.liked && 'fill-current')} />
          </TombolAksi>

          <TombolAksi
            label="Komentar"
            jumlah={post.comment_count}
            onClick={() => setKomentarTerbuka((n) => !n)}
          >
            <MessageCircle className="size-5" />
          </TombolAksi>
        </div>

        {komentarTerbuka ? <Komentar postId={post.id} /> : null}
      </div>
    </article>
  )
}

function TombolAksi({
  children,
  label,
  jumlah,
  aktif,
  onClick,
}: {
  children: React.ReactNode
  label: string
  jumlah: number
  aktif?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={aktif}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-[13px] tabular-nums transition',
        aktif ? 'text-danger' : 'text-ink hover:bg-sunken',
      )}
    >
      {children}
      {/* Angka nol tidak ditampilkan. Deretan nol pada lini masa yang masih
          sepi membuatnya terlihat lebih sepi daripada keadaannya. */}
      {jumlah > 0 ? <span>{jumlah}</span> : null}
    </button>
  )
}

/**
 * GaleriPostingan menyusun gambar menurut jumlahnya.
 *
 * Satu gambar tampil penuh; dua sampai empat dibagi rata. Kisi tetap untuk
 * semua jumlah membuat kiriman bergambar tunggal — yang paling sering —
 * tampil kecil di tengah ruang kosong.
 */
function GaleriPostingan({ gambar }: { gambar: string[] }) {
  const [dibuka, setDibuka] = useState<string | null>(null)

  return (
    <>
      <div
        className={cn(
          'mt-3 grid gap-1 overflow-hidden rounded-xl border border-line',
          gambar.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
        )}
      >
        {gambar.map((url, index) => (
          <button
            key={url}
            type="button"
            onClick={() => setDibuka(url)}
            aria-label={`Lihat gambar ${index + 1} lebih besar`}
            className={cn('overflow-hidden bg-sunken', gambar.length === 3 && index === 0 && 'col-span-2')}
          >
            <img
              src={url}
              alt=""
              loading="lazy"
              className={cn(
                'w-full object-cover transition duration-300 hover:scale-[1.02]',
                gambar.length === 1 ? 'max-h-[26rem]' : 'aspect-square',
              )}
            />
          </button>
        ))}
      </div>

      {dibuka ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setDibuka(null)}
          className="fixed inset-0 z-50 grid place-items-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <img src={dibuka} alt="" className="max-h-[85vh] max-w-full rounded-xl object-contain" />
        </div>
      ) : null}
    </>
  )
}

function Komentar({ postId }: { postId: string }) {
  const { data, isLoading } = usePostComments(postId, true)
  const tambah = useAddComment(postId)
  const hapus = useDeleteComment(postId)
  const toast = useToast()
  const [isi, setIsi] = useState('')

  const kirim = () => {
    const teks = isi.trim()
    if (!teks) return

    tambah.mutate(teks, {
      onSuccess: () => setIsi(''),
      onError: (error) =>
        toast.error('Komentar gagal dikirim', error instanceof ApiError ? error.message : undefined),
    })
  }

  return (
    <div className="mt-3 space-y-3">
      {isLoading ? (
        <p className="text-sm text-muted">Memuat komentar…</p>
      ) : (
        (data?.comments ?? []).map((k) => (
          <div key={k.id} className="flex items-start gap-2.5">
            <Avatar name={k.author.full_name} src={k.author.avatar_url} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-bold text-ink">{k.author.full_name}</span>
                <span className="text-sm text-subtle">{formatRelative(k.created_at)}</span>
              </div>
              <p className="whitespace-pre-line text-sm text-muted">{k.body}</p>
            </div>
            {k.can_delete ? (
              <button
                type="button"
                onClick={() => hapus.mutate(k.id)}
                aria-label="Hapus komentar"
                className="grid size-7 shrink-0 place-items-center rounded-full text-subtle transition hover:bg-sunken hover:text-danger"
              >
                <Trash2 className="size-3.5" />
              </button>
            ) : null}
          </div>
        ))
      )}

      <div className="flex gap-2">
        <Input
          value={isi}
          onChange={(e) => setIsi(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              kirim()
            }
          }}
          placeholder="Tulis komentar…"
          aria-label="Tulis komentar"
        />
        <Button onClick={kirim} disabled={tambah.isPending || !isi.trim()}>
          Kirim
        </Button>
      </div>
    </div>
  )
}
